import React, { useEffect, useMemo, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { motion } from "framer-motion";
import { ArrowRight, MessageSquare, Clock, Trash2, Plus, MoreVertical, Pencil, Receipt, CheckCircle2, Sparkles, Eye, EyeOff, Search } from "lucide-react";
import api from "@/lib/api";
import { usePricesHidden } from "@/lib/priceVisibility";
import StatusBadge from "@/components/StatusBadge";
import { Button } from "@/components/ui/button";
import { Checkbox } from "@/components/ui/checkbox";
import { Tabs, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { DropdownMenu, DropdownMenuContent, DropdownMenuItem, DropdownMenuSeparator, DropdownMenuTrigger } from "@/components/ui/dropdown-menu";
import { toast } from "sonner";

const STATUS_FILTERS = [
    { key: "all", label: "Alle" },
    { key: "needs_attention", label: "Met opmerkingen" },
    { key: "concept", label: "Concept (geen datum)" },
    { key: "pending_approval", label: "Wacht op goedkeuring" },
    { key: "approved_scheduled", label: "Ingepland" },
    { key: "in_production", label: "In productie" },
    { key: "delivered", label: "Opgeleverd" },
    { key: "to_invoice", label: "Te factureren" },
    { key: "rejected", label: "Afgewezen" },
];

const fmtDate = (d) => {
    if (!d) return "—";
    const dt = typeof d === "string" ? new Date(d.length <= 10 ? `${d}T00:00:00` : d) : d;
    if (isNaN(dt)) return d;
    return dt.toLocaleDateString("nl-BE", { day: "2-digit", month: "2-digit", year: "numeric" });
};

export default function AdminDashboard() {
    const navigate = useNavigate();
    const [stats, setStats] = useState(null);
    const [requests, setRequests] = useState([]);
    const [openComments, setOpenComments] = useState({});
    const [filter, setFilter] = useState("all");
    const [search, setSearch] = useState("");
    const [revenueHidden, setRevenueHidden] = useState(() => localStorage.getItem("vsf_admin_revenue_hidden") === "1");
    const [pricesHidden] = usePricesHidden();
    const [selected, setSelected] = useState(new Set());
    const [bulkDeleting, setBulkDeleting] = useState(false);

    const reload = () => {
        api.get("/admin/stats").then((r) => setStats(r.data));
        api.get("/requests").then((r) => setRequests(r.data));
        api.get("/admin/open-comments-counts").then((r) => setOpenComments(r.data || {})).catch(() => {});
    };

    useEffect(() => { reload(); }, []);

    const totalOpenComments = useMemo(() => Object.values(openComments).reduce((a, b) => a + b, 0), [openComments]);
    const projectsWithComments = useMemo(() => Object.keys(openComments).filter((k) => openComments[k] > 0).length, [openComments]);

    // Sort projects: those with unresolved comments float to the top, then by upcoming/created date desc.
    const sortedRequests = useMemo(() => {
        return [...requests].sort((a, b) => {
            const ca = openComments[a.id] || 0;
            const cb = openComments[b.id] || 0;
            if (ca !== cb) return cb - ca;
            return new Date(b.created_at) - new Date(a.created_at);
        });
    }, [requests, openComments]);

    const filtered = useMemo(() => {
        let base;
        if (filter === "all") base = sortedRequests;
        else if (filter === "needs_attention") base = sortedRequests.filter((r) => (openComments[r.id] || 0) > 0);
        else if (filter === "concept") base = sortedRequests.filter((r) => !r.scheduled_date && !["delivered", "rejected"].includes(r.status));
        else if (filter === "to_invoice") base = sortedRequests.filter((r) => r.status === "delivered" && !r.invoiced);
        else base = sortedRequests.filter((r) => r.status === filter);
        const q = search.trim().toLowerCase();
        if (!q) return base;
        return base.filter((r) =>
            [r.address_street, r.address_city, r.address_postal_code, r.user_name, r.user_company, r.user_email]
                .some((v) => (v || "").toLowerCase().includes(q))
        );
    }, [sortedRequests, openComments, filter, search]);

    const toggleInvoiced = async (req, makeInvoiced) => {
        try {
            await api.patch(`/requests/${req.id}/invoiced`, { invoiced: makeInvoiced });
            setRequests((prev) => prev.map((r) => r.id === req.id ? { ...r, invoiced: makeInvoiced, invoiced_at: makeInvoiced ? new Date().toISOString() : null } : r));
            toast.success(makeInvoiced ? "Gemarkeerd als gefactureerd" : "Gemarkeerd als niet gefactureerd");
        } catch (e) {
            toast.error(e.response?.data?.detail || "Kon status niet aanpassen");
        }
    };

    return (
        <div className="min-h-screen bg-[#FAFAFA] text-zinc-950 font-body pb-20" data-testid="admin-dashboard">
            <div className="max-w-[1400px] mx-auto px-6 lg:px-10 pt-10">
                <div className="flex flex-col md:flex-row md:items-end justify-between gap-4 mb-10">
                    <div>
                        <div className="text-xs uppercase tracking-[0.3em] text-[#E5A93D] font-semibold mb-3">Control room</div>
                        <h1 className="font-display font-black text-4xl lg:text-5xl tracking-tighter">Admin overzicht.</h1>
                    </div>
                    <button
                        onClick={() => navigate("/new-request")}
                        className="inline-flex items-center gap-2 h-11 px-5 bg-[#E5A93D] hover:bg-[#C8922C] text-black font-bold rounded-sm active:scale-[0.98] transition self-start md:self-auto"
                        data-testid="admin-new-request-btn"
                    >
                        <Plus className="h-4 w-4" /> Nieuwe aanvraag
                    </button>
                </div>

                {/* Stats Grid */}
                {stats && (
                    <div className="grid grid-cols-2 lg:grid-cols-4 gap-4 mb-12">
                        <button
                            type="button"
                            onClick={() => {
                                const conceptCount = requests.filter((r) => !r.scheduled_date && !["delivered", "rejected"].includes(r.status)).length;
                                if (conceptCount > 0) setFilter("concept");
                            }}
                            className="text-left bg-[#FFFFFF] border border-[#7C3AED]/30 hover:border-[#7C3AED] rounded-md p-6 transition-all"
                            data-testid="admin-stat-concept"
                        >
                            <div className="text-xs uppercase tracking-[0.2em] text-[#7C3AED] mb-2">Concept (geen datum)</div>
                            <div className="font-display font-black text-3xl text-zinc-950">
                                {requests.filter((r) => !r.scheduled_date && !["delivered", "rejected"].includes(r.status)).length}
                            </div>
                        </button>
                        <StatCard label="Ingepland" value={stats.scheduled} />
                        <StatCard label="In productie" value={stats.in_production} />
                        <StatCard label="Opgeleverd" value={stats.delivered} />
                        <StatCard label="Nieuwe aanvragen" value={stats.pending} accent />
                        <StatCard label="Totaal projecten" value={stats.total_requests} />
                        <button
                            type="button"
                            onClick={() => totalOpenComments > 0 && setFilter("needs_attention")}
                            className={`text-left rounded-md p-6 transition-all ${totalOpenComments > 0 ? "bg-[#EF4444]/10 border border-[#EF4444]/40 hover:border-[#EF4444] cursor-pointer" : "bg-[#FFFFFF] border border-zinc-100"}`}
                            data-testid="admin-stat-open-comments"
                        >
                            <div className={`text-xs uppercase tracking-[0.2em] mb-2 flex items-center gap-1.5 ${totalOpenComments > 0 ? "text-[#EF4444]" : "text-zinc-500"}`}>
                                <MessageSquare className="h-3 w-3" />
                                Onopgeloste opmerkingen
                            </div>
                            <div className={`font-display font-black text-3xl ${totalOpenComments > 0 ? "text-[#EF4444]" : "text-zinc-950"}`}>
                                {totalOpenComments}
                            </div>
                            {projectsWithComments > 0 && (
                                <div className="text-[11px] text-zinc-500 mt-1">over {projectsWithComments} project{projectsWithComments === 1 ? "" : "en"}</div>
                            )}
                        </button>
                        <div className="bg-[#FFFFFF] border border-[#E5A93D]/30 rounded-md p-6">
                            <div className="text-xs uppercase tracking-[0.2em] text-[#E5A93D] mb-2 flex items-center justify-between gap-2">
                                <span>Omzet (goedgekeurd)</span>
                                <button
                                    type="button"
                                    onClick={() => {
                                        setRevenueHidden((v) => {
                                            localStorage.setItem("vsf_admin_revenue_hidden", v ? "0" : "1");
                                            return !v;
                                        });
                                    }}
                                    className="text-zinc-400 hover:text-zinc-950 transition"
                                    title={revenueHidden ? "Toon omzet" : "Verberg omzet"}
                                    data-testid="admin-revenue-toggle"
                                >
                                    {revenueHidden ? <EyeOff className="h-4 w-4" /> : <Eye className="h-4 w-4" />}
                                </button>
                            </div>
                            <div className="font-display font-black text-3xl text-zinc-950" data-testid="admin-revenue-value">
                                {(revenueHidden || pricesHidden) ? "€ ••••••" : `€${stats.revenue.toFixed(2)}`}
                            </div>
                        </div>
                    </div>
                )}

                {/* Zoekbalk: adres of makelaar */}
                <div className="mb-4 relative max-w-md">
                    <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-zinc-400 pointer-events-none" />
                    <input
                        value={search}
                        onChange={(e) => setSearch(e.target.value)}
                        placeholder="Zoek op adres, stad of makelaar…"
                        className="w-full h-11 pl-10 pr-10 bg-white border border-zinc-200 rounded-sm text-sm focus:outline-none focus:border-[#E5A93D] placeholder:text-zinc-400"
                        data-testid="admin-search-input"
                    />
                    {search && (
                        <button
                            onClick={() => setSearch("")}
                            className="absolute right-2 top-1/2 -translate-y-1/2 h-6 w-6 flex items-center justify-center rounded-sm text-zinc-400 hover:text-zinc-950 hover:bg-zinc-100"
                            data-testid="admin-search-clear"
                            title="Wis zoekopdracht"
                        >
                            ×
                        </button>
                    )}
                </div>

                {/* Filter Tabs + bulk action bar */}
                <div className="mb-6 flex items-center justify-between gap-4 flex-wrap">
                    <div className="overflow-x-auto no-scrollbar">
                        <Tabs value={filter} onValueChange={setFilter}>
                            <TabsList className="bg-[#FFFFFF] border border-zinc-100 p-1 h-auto flex-wrap">
                                {STATUS_FILTERS.map((s) => {
                                    const count = s.key === "all"
                                        ? requests.length
                                        : s.key === "needs_attention"
                                            ? projectsWithComments
                                            : s.key === "concept"
                                                ? requests.filter((r) => !r.scheduled_date && !["delivered", "rejected"].includes(r.status)).length
                                                : requests.filter((r) => r.status === s.key).length;
                                    return (
                                        <TabsTrigger
                                            key={s.key}
                                            value={s.key}
                                            className={`rounded-sm ${s.key === "needs_attention" && projectsWithComments > 0 ? "data-[state=active]:bg-[#EF4444] data-[state=active]:text-black text-[#EF4444]" : s.key === "concept" ? "data-[state=active]:bg-[#7C3AED] data-[state=active]:text-black text-[#7C3AED]" : "data-[state=active]:bg-[#E5A93D] data-[state=active]:text-black text-zinc-500"}`}
                                            data-testid={`filter-${s.key}`}
                                        >
                                            {s.key === "needs_attention" && <MessageSquare className="h-3 w-3 mr-1" />}
                                            {s.label} ({count})
                                        </TabsTrigger>
                                    );
                                })}
                            </TabsList>
                        </Tabs>
                    </div>
                    {selected.size > 0 && (
                        <div className="flex items-center gap-3 bg-[#EF4444]/10 border border-[#EF4444]/40 rounded-sm px-3 py-2" data-testid="bulk-action-bar">
                            <span className="text-sm text-[#EF4444] font-bold">{selected.size} geselecteerd</span>
                            <button
                                onClick={() => setSelected(new Set())}
                                className="text-xs text-zinc-500 hover:text-zinc-950"
                                data-testid="bulk-clear-btn"
                            >
                                Wis selectie
                            </button>
                            <Button
                                onClick={async () => {
                                    if (!window.confirm(`${selected.size} aanvraag/aanvragen permanent verwijderen? Dit kan niet ongedaan gemaakt worden.`)) return;
                                    setBulkDeleting(true);
                                    try {
                                        const { data } = await api.post("/admin/requests/bulk-delete", { ids: Array.from(selected) });
                                        toast.success(`${data.deleted_count} aanvraag/aanvragen verwijderd`);
                                        setSelected(new Set());
                                        reload();
                                    } catch (e) {
                                        toast.error(e.response?.data?.detail || "Verwijderen mislukt");
                                    }
                                    setBulkDeleting(false);
                                }}
                                disabled={bulkDeleting}
                                className="h-8 bg-[#EF4444] hover:bg-[#DC2626] text-zinc-950 font-bold text-xs rounded-sm"
                                data-testid="bulk-delete-btn"
                            >
                                <Trash2 className="h-3.5 w-3.5 mr-1" />
                                {bulkDeleting ? "Bezig..." : "Verwijder geselecteerde"}
                            </Button>
                        </div>
                    )}
                </div>

                {/* Select-all toggle */}
                {filtered.length > 0 && (
                    <div className="mb-3 flex items-center gap-2 text-xs text-zinc-500">
                        <Checkbox
                            checked={filtered.length > 0 && filtered.every((r) => selected.has(r.id))}
                            onCheckedChange={(checked) => {
                                if (checked) {
                                    setSelected(new Set([...selected, ...filtered.map((r) => r.id)]));
                                } else {
                                    const next = new Set(selected);
                                    filtered.forEach((r) => next.delete(r.id));
                                    setSelected(next);
                                }
                            }}
                            data-testid="select-all-checkbox"
                            id="select-all"
                        />
                        <label htmlFor="select-all" className="cursor-pointer select-none">
                            {filtered.every((r) => selected.has(r.id)) ? "Alles deselecteren" : "Selecteer alles"} ({filtered.length})
                        </label>
                    </div>
                )}

                {/* List */}
                <div className="space-y-3">
                    {filtered.length === 0 && (
                        <div className="text-center text-zinc-500 py-16 border border-dashed border-zinc-200 rounded-md">Geen aanvragen</div>
                    )}
                    {filtered.map((r, i) => {
                        const openCount = openComments[r.id] || 0;
                        const isSelected = selected.has(r.id);
                        const toggleSelect = (e) => {
                            e.stopPropagation();
                            const next = new Set(selected);
                            if (isSelected) next.delete(r.id);
                            else next.add(r.id);
                            setSelected(next);
                        };
                        return (
                            <motion.div
                                key={r.id}
                                initial={{ opacity: 0, y: 6 }}
                                animate={{ opacity: 1, y: 0 }}
                                transition={{ delay: i * 0.03 }}
                                onClick={() => navigate(`/admin/projects/${r.id}`)}
                                className={`group rounded-md p-6 cursor-pointer transition-all ${isSelected ? "bg-[#EF4444]/5 border border-[#EF4444]" : openCount > 0 ? "bg-[#FFFFFF] border border-[#EF4444]/40 hover:border-[#EF4444]" : "bg-[#FFFFFF] border border-zinc-100 hover:border-zinc-400"}`}
                                data-testid={`admin-request-row-${r.id}`}
                            >
                                <div className="flex flex-col lg:flex-row lg:items-center gap-4">
                                    <div className="flex items-start gap-3 flex-1 min-w-0">
                                        <div onClick={toggleSelect} className="pt-1 cursor-pointer flex-shrink-0">
                                            <Checkbox
                                                checked={isSelected}
                                                onCheckedChange={() => {
                                                    const next = new Set(selected);
                                                    if (isSelected) next.delete(r.id);
                                                    else next.add(r.id);
                                                    setSelected(next);
                                                }}
                                                onClick={(e) => e.stopPropagation()}
                                                data-testid={`row-checkbox-${r.id}`}
                                            />
                                        </div>
                                        <div className="flex-1 min-w-0">
                                        <div className="flex items-center gap-3 mb-2 flex-wrap">
                                            <StatusBadge status={r.status} />
                                            {r.invoiced && (
                                                <span
                                                    className="inline-flex items-center gap-1 text-[10px] uppercase tracking-wider font-bold px-2 py-0.5 rounded-sm bg-emerald-500/15 border border-emerald-500/40 text-emerald-600"
                                                    title="Deze aanvraag is gefactureerd"
                                                    data-testid={`invoiced-badge-${r.id}`}
                                                >
                                                    <Receipt className="h-2.5 w-2.5" /> Gefactureerd
                                                </span>
                                            )}
                                            {openCount > 0 && (
                                                <span
                                                    className="inline-flex items-center gap-1 text-[10px] uppercase tracking-wider font-bold px-2 py-0.5 rounded-sm bg-[#EF4444]/15 border border-[#EF4444]/40 text-[#EF4444]"
                                                    title={`${openCount} onopgeloste opmerking${openCount === 1 ? "" : "en"}`}
                                                    data-testid={`open-comments-badge-${r.id}`}
                                                >
                                                    <MessageSquare className="h-2.5 w-2.5" />
                                                    {openCount} {openCount === 1 ? "opmerking" : "opmerkingen"}
                                                </span>
                                            )}
                                            {(r.ai_edits_count || 0) > 0 && (
                                                <span
                                                    className="inline-flex items-center gap-1 text-[10px] uppercase tracking-wider font-bold px-2 py-0.5 rounded-sm bg-[#7C3AED]/15 border border-[#7C3AED]/40 text-[#7C3AED]"
                                                    title={`${r.ai_edits_count} AI-bewerking${r.ai_edits_count === 1 ? "" : "en"} · ${r.ai_approved_count || 0} foto${(r.ai_approved_count || 0) === 1 ? "" : "'s"} vervangen · totaal kost ${pricesHidden ? "€ ••••" : `€${Number(r.ai_total_cost_eur || 0).toFixed(2)}`}`}
                                                    data-testid={`ai-edits-badge-${r.id}`}
                                                >
                                                    <Sparkles className="h-2.5 w-2.5" />
                                                    {r.ai_edits_count} AI · {r.ai_approved_count || 0} ✓ · {pricesHidden ? "€ ••••" : `€${Number(r.ai_total_cost_eur || 0).toFixed(2)}`}
                                                </span>
                                            )}
                                            <span className="text-xs text-zinc-500">
                                                {fmtDate(r.created_at)}
                                            </span>
                                            <span className="text-xs text-zinc-500">•</span>
                                            <span className="text-xs text-[#E5A93D]">{r.user_name}</span>
                                        </div>
                                        <h3 className="font-display font-bold text-lg text-zinc-950 group-hover:text-[#E5A93D] transition-colors">
                                            {r.address_street}, {r.address_city}
                                        </h3>
                                        <div className="flex flex-wrap items-center gap-x-2 gap-y-1 text-sm text-zinc-500 mt-1">
                                            <span>{r.items.length} {r.items.length === 1 ? "product" : "producten"}</span>
                                            <span className="text-zinc-400">•</span>
                                            {r.scheduled_date || r.preferred_date ? (
                                                <span className="inline-flex items-center gap-1.5">
                                                    {fmtDate(r.scheduled_date || r.preferred_date)}
                                                    <Clock className="h-3 w-3 text-[#E5A93D]" />
                                                    {r.scheduled_time || r.preferred_time}
                                                </span>
                                            ) : (
                                                <span className="italic text-zinc-500">Datum nog te bepalen</span>
                                            )}
                                        </div>
                                        </div>
                                    </div>
                                    <div className="flex items-center gap-3">
                                        <div className="text-right">
                                            <div className="font-display font-black text-2xl text-[#E5A93D]" data-testid={`row-price-${r.id}`}>{pricesHidden ? "€ ••••" : `€${r.total_price}`}</div>
                                        </div>
                                        <DropdownMenu>
                                            <DropdownMenuTrigger asChild>
                                                <button
                                                    onClick={(e) => e.stopPropagation()}
                                                    className="p-2 rounded-sm hover:bg-zinc-100 text-zinc-500 hover:text-zinc-950 transition"
                                                    data-testid={`row-menu-${r.id}`}
                                                    aria-label="Meer acties"
                                                >
                                                    <MoreVertical className="h-4 w-4" />
                                                </button>
                                            </DropdownMenuTrigger>
                                            <DropdownMenuContent
                                                align="end"
                                                onClick={(e) => e.stopPropagation()}
                                                className="bg-[#FFFFFF] border border-zinc-200 text-zinc-950"
                                            >
                                                <DropdownMenuItem
                                                    onClick={(e) => { e.stopPropagation(); navigate(`/admin/projects/${r.id}`); }}
                                                    className="cursor-pointer focus:bg-zinc-100 focus:text-zinc-950 gap-2"
                                                    data-testid={`row-menu-edit-${r.id}`}
                                                >
                                                    <Pencil className="h-4 w-4 text-zinc-500" /> Bewerken
                                                </DropdownMenuItem>
                                                <DropdownMenuSeparator className="bg-zinc-100" />
                                                {r.invoiced ? (
                                                    <DropdownMenuItem
                                                        onClick={(e) => { e.stopPropagation(); toggleInvoiced(r, false); }}
                                                        className="cursor-pointer focus:bg-zinc-100 focus:text-zinc-950 gap-2"
                                                        data-testid={`row-menu-uninvoice-${r.id}`}
                                                    >
                                                        <Receipt className="h-4 w-4 text-zinc-500" /> Markeer als niet gefactureerd
                                                    </DropdownMenuItem>
                                                ) : (
                                                    <DropdownMenuItem
                                                        onClick={(e) => { e.stopPropagation(); toggleInvoiced(r, true); }}
                                                        className="cursor-pointer focus:bg-emerald-500/10 focus:text-emerald-600 gap-2"
                                                        data-testid={`row-menu-invoice-${r.id}`}
                                                    >
                                                        <CheckCircle2 className="h-4 w-4 text-emerald-600" /> Markeer als gefactureerd
                                                    </DropdownMenuItem>
                                                )}
                                            </DropdownMenuContent>
                                        </DropdownMenu>
                                        <ArrowRight className="h-5 w-5 text-zinc-400 group-hover:text-[#E5A93D] group-hover:translate-x-1 transition-all" />
                                    </div>
                                </div>
                            </motion.div>
                        );
                    })}
                </div>
            </div>
        </div>
    );
}

function StatCard({ label, value, accent }) {
    return (
        <motion.div
            initial={{ opacity: 0, y: 8 }}
            animate={{ opacity: 1, y: 0 }}
            className={`rounded-md p-6 ${accent ? "bg-[#E5A93D]/10 border border-[#E5A93D]/30" : "bg-[#FFFFFF] border border-zinc-100"}`}
            data-testid={`admin-stat-${label}`}
        >
            <div className="text-xs uppercase tracking-[0.2em] text-zinc-500 mb-2">{label}</div>
            <div className={`font-display font-black text-3xl ${accent ? "text-[#E5A93D]" : "text-zinc-950"}`}>{value}</div>
        </motion.div>
    );
}
